import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { queryPipelineStages } from '@/features/pipelines/data';
import {
  buildStageNameFilters,
  buildStagePipelineFilters,
} from '@/features/pipelines/hooks/stagePageFilters';
import {
  buildCountedDropdownOptions,
  buildStringArrayCountMap,
  buildStringCountMap,
} from '@/features/shared/components/tableSystem/filterOptionCounts';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { BuildStatus } from '@flowqa/shared';

type UseStagesPageFilterOptionCountsArgs = {
  effectiveEndDay: string | null;
  effectiveProjectId: string | null;
  effectiveStartDay: string | null;
  hasLatestTimestamp: boolean;
  isAuthenticated: boolean;
  isLatestMode: boolean;
  pipelineOptions: readonly TableDropdownOption[];
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  stageOptions: readonly TableDropdownOption[];
  stageSearchValue: string;
  tagOptions: readonly TableDropdownOption[];
};

type CountedStageFilterOptions = {
  countedPipelineOptions: TableDropdownOption[];
  countedStageOptions: TableDropdownOption[];
  countedTagOptions: TableDropdownOption[];
};

export const useStagesPageFilterOptionCounts = ({
  effectiveEndDay,
  effectiveProjectId,
  effectiveStartDay,
  hasLatestTimestamp,
  isAuthenticated,
  isLatestMode,
  pipelineOptions,
  selectedOrganisationId,
  selectedPipelineIds,
  selectedStageNames,
  selectedStatuses,
  selectedTags,
  stageOptions,
  stageSearchValue,
  tagOptions,
}: UseStagesPageFilterOptionCountsArgs): CountedStageFilterOptions => {
  const stageCountDateFilters = useMemo(
    () =>
      buildDateRangeFilters(
        'startedAt',
        effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
        effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
      ),
    [effectiveEndDay, effectiveStartDay],
  );
  const pipelineCountStageFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('stageName', stageSearchValue),
        buildInFilter('status', selectedStatuses),
        buildStageNameFilters(selectedStageNames),
        buildInFilter('tags', selectedTags),
        stageCountDateFilters,
      ),
    [selectedStageNames, selectedStatuses, selectedTags, stageCountDateFilters, stageSearchValue],
  );
  const stageNameCountStageFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('stageName', stageSearchValue),
        buildInFilter('status', selectedStatuses),
        buildStagePipelineFilters(selectedPipelineIds),
        buildInFilter('tags', selectedTags),
        stageCountDateFilters,
      ),
    [selectedPipelineIds, selectedStatuses, selectedTags, stageCountDateFilters, stageSearchValue],
  );
  const tagCountStageFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('stageName', stageSearchValue),
        buildInFilter('status', selectedStatuses),
        buildStagePipelineFilters(selectedPipelineIds),
        buildStageNameFilters(selectedStageNames),
        stageCountDateFilters,
      ),
    [
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
      stageCountDateFilters,
      stageSearchValue,
    ],
  );
  const pipelineCountStagesQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'pipeline-counts',
      selectedOrganisationId,
      effectiveProjectId,
      JSON.stringify(pipelineCountStageFilters),
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: pipelineCountStageFilters,
          }),
      ),
  });
  const stageNameCountStagesQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'stage-name-counts',
      selectedOrganisationId,
      effectiveProjectId,
      JSON.stringify(stageNameCountStageFilters),
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: stageNameCountStageFilters,
          }),
      ),
  });
  const tagCountStagesQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'tag-counts',
      selectedOrganisationId,
      effectiveProjectId,
      JSON.stringify(tagCountStageFilters),
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: tagCountStageFilters,
          }),
      ),
  });

  return useMemo(
    () => ({
      countedPipelineOptions: buildCountedDropdownOptions(
        pipelineOptions,
        buildStringCountMap(
          pipelineCountStagesQuery.data?.items ?? [],
          (stage) => stage.pipelineId,
        ),
      ),
      countedStageOptions: buildCountedDropdownOptions(
        stageOptions,
        buildStringCountMap(
          stageNameCountStagesQuery.data?.items ?? [],
          (stage) => stage.stageName,
        ),
      ),
      countedTagOptions: buildCountedDropdownOptions(
        tagOptions,
        buildStringArrayCountMap(tagCountStagesQuery.data?.items ?? [], (stage) => stage.tags),
      ),
    }),
    [
      pipelineCountStagesQuery.data?.items,
      pipelineOptions,
      stageNameCountStagesQuery.data?.items,
      stageOptions,
      tagCountStagesQuery.data?.items,
      tagOptions,
    ],
  );
};
